import '../styles/ExtractionResultPanel.css';
import React, { useEffect, useRef, useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const SKELETON_ROWS = ['a', 'b', 'c'];

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readCompletedRunId() {
  if (typeof window === 'undefined') return '';
  try {
    const params = new URLSearchParams(window.location.search);
    return (params.get('run') || '').trim();
  } catch (err) {
    return '';
  }
}

function readRunTitle() {
  if (typeof window === 'undefined') return '';
  try {
    const params = new URLSearchParams(window.location.search);
    return (params.get('title') || '').trim();
  } catch (err) {
    return '';
  }
}

/* ResultTotalReadout — one count-up from 0 to the final total. */
function ResultTotalReadout({ target, live, loading }) {
  const [display, setDisplay] = useState(target);
  const animatedFor = useRef(null);

  useEffect(() => {
    if (animatedFor.current === target) return undefined;
    animatedFor.current = target;
    if (prefersReducedMotion() || target === null || target === undefined) {
      setDisplay(target);
      return undefined;
    }
    let frame = 0;
    let start = null;
    const duration = 900;
    setDisplay(0);
    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(target * eased));
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        setDisplay(target);
      }
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  const finalLabel = target === null || target === undefined ? '—' : String(target);
  const shown = loading ? '—' : display === null || display === undefined ? '—' : String(display);
  const muted = loading || target === null || target === undefined;

  return (
    <div className={`erp-total${muted ? ' erp-total-muted' : ''}`} role="status" aria-live="polite">
      <span
        className={`erp-dot${live ? ' erp-dot-live' : ''}`}
        aria-hidden="true"
      />
      <span className="erp-total-figure">
        <span className="erp-total-label">Result total</span>
        <span className="erp-total-number">{shown}</span>
      </span>
      <span className="erp-total-caption">{live ? 'Live' : 'Leads'}</span>
      <span className="erp-visually-hidden">
        {muted ? 'Result total unavailable.' : `Result total: ${finalLabel} leads.`}
      </span>
    </div>
  );
}

/* SavedRunConfirmation — only after a completed run. */
function SavedRunConfirmation() {
  return (
    <div className="erp-saved">
      <span className="erp-saved-mark" aria-hidden="true" />
      <p className="erp-saved-text">
        <strong>This run is saved.</strong> Its lead list is reachable from Leads.
      </p>
      <a className="erp-leads-link" href="/leads">
        Open saved lead lists
        <svg width="14" height="10" viewBox="0 0 14 10" aria-hidden="true" focusable="false">
          <path d="M0 5h12M9 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      </a>
    </div>
  );
}

function LeadRowsPanel({ leads }) {
  return (
    <div className="erp-rows">
      <div className="erp-rows-head" aria-hidden="true">
        <span>No.</span>
        <span>Name</span>
        <span>Role</span>
        <span>Company</span>
        <span>Location</span>
        <span>Profile URL</span>
      </div>
      {leads.map((lead, index) => {
        const no = String(index + 1).padStart(2, '0');
        return (
          <dl className="erp-row" key={lead.id} aria-label={`Lead ${no}`}>
            <span className="erp-row-index" aria-hidden="true">{no}</span>
            <div className="erp-field">
              <dt>Name</dt>
              <dd className="erp-name">{lead.name}</dd>
            </div>
            <div className="erp-field">
              <dt>Role</dt>
              <dd>{lead.role}</dd>
            </div>
            <div className="erp-field">
              <dt>Company</dt>
              <dd>{lead.company}</dd>
            </div>
            <div className="erp-field">
              <dt>Location</dt>
              <dd>{lead.location}</dd>
            </div>
            <div className="erp-field">
              <dt>Profile URL</dt>
              <dd>
                {lead.profileUrl ? (
                  <a
                    className="erp-profile"
                    href={`https://${String(lead.profileUrl).replace(/^https?:\/\//, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {lead.profileUrl}
                    <span className="erp-visually-hidden"> (opens in a new tab)</span>
                  </a>
                ) : (
                  '—'
                )}
              </dd>
            </div>
          </dl>
        );
      })}
    </div>
  );
}

function ExtractionResultPanel() {
  const { data, loading, error, refresh } = useProjectCollection('leads');
  const { data: runsData, refresh: refreshRuns } = useProjectCollection('extraction_runs');
  const [retrying, setRetrying] = useState(false);
  const [completedRunId, setCompletedRunId] = useState(readCompletedRunId);
  const [runTitle, setRunTitle] = useState(readRunTitle);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onComplete = (event) => {
      const detail = (event && event.detail) || {};
      setCompletedRunId(String(detail.run || '').trim());
      setRunTitle(String(detail.title || '').trim());
      refresh();
      refreshRuns();
    };
    window.addEventListener('extraction:run-complete', onComplete);
    return () => window.removeEventListener('extraction:run-complete', onComplete);
  }, [refresh, refreshRuns]);

  const leads = Array.isArray(data) ? data : [];
  const runs = Array.isArray(runsData) ? runsData : [];
  const completedRun = completedRunId
    ? runs.find((run) => run.id === completedRunId) || null
    : null;
  const scopedLeads = completedRunId
    ? leads.filter((lead) => lead.runId === completedRunId)
    : leads;
  const hasCompletedRun = Boolean(completedRunId) && !loading && !error;
  const total =
    loading || error || !hasCompletedRun || !completedRun
      ? null
      : Number(completedRun.resultTotal) || 0;

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await refresh();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <section className="erp-root" aria-labelledby="erp-title">
      <div className="erp-panel">
        <header className="erp-head">
          <div className="erp-head-intro">
            <p className="erp-eyebrow">Extraction result</p>
            <h2 className="erp-title" id="erp-title">Returned leads</h2>
            {hasCompletedRun && runTitle ? (
              <p className="erp-scope">
                <span className="erp-scope-label">Run</span>
                <span className="erp-scope-value">{runTitle}</span>
              </p>
            ) : null}
          </div>
          <ResultTotalReadout
            target={hasCompletedRun ? total : null}
            live={loading}
            loading={loading}
          />
        </header>

        {hasCompletedRun ? <SavedRunConfirmation /> : null}

        <div className="erp-body">
          {error ? (
            <>
              <p className="erp-error" role="alert">
                <span className="erp-error-label">Error</span>
                Saved leads could not be loaded.
              </p>
              <button
                type="button"
                className="erp-retry"
                onClick={retry}
                disabled={retrying}
              >
                {retrying ? 'Retrying…' : 'Retry loading leads'}
              </button>
            </>
          ) : loading ? (
            <div className="erp-rows" aria-busy="true" aria-live="polite">
              <p className="erp-visually-hidden">Loading returned leads.</p>
              {SKELETON_ROWS.map((key) => (
                <div className="erp-skeleton-row" key={key} aria-hidden="true">
                  <span className="erp-bar erp-bar-mid" />
                  <span className="erp-bar erp-bar-short" />
                  <span className="erp-bar" />
                  <span className="erp-bar erp-bar-short" />
                </div>
              ))}
            </div>
          ) : leads.length === 0 ? (
            <div className="erp-notice">
              <p className="erp-notice-title">No leads yet</p>
              <hr className="erp-notice-rule" />
              <p className="erp-notice-text">
                No leads have been returned yet. Start an extraction to build the first lead list.
              </p>
              <a className="erp-link-quiet" href="/extraction">
                Go to Extraction
                <svg width="14" height="10" viewBox="0 0 14 10" aria-hidden="true" focusable="false">
                  <path d="M0 5h12M9 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.2" />
                </svg>
              </a>
            </div>
          ) : scopedLeads.length === 0 ? (
            <div className="erp-notice">
              <p className="erp-notice-title">No leads for this run</p>
              <hr className="erp-notice-rule" />
              <p className="erp-notice-text">
                The completed run returned no leads. Earlier saved lists are unaffected.
              </p>
              <a className="erp-link-quiet" href="/leads">
                Open saved lead lists
                <svg width="14" height="10" viewBox="0 0 14 10" aria-hidden="true" focusable="false">
                  <path d="M0 5h12M9 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.2" />
                </svg>
              </a>
            </div>
          ) : (
            <>
              {!hasCompletedRun ? (
                <p className="erp-eyebrow erp-stored-label">Stored leads</p>
              ) : null}
              <LeadRowsPanel leads={scopedLeads} />
            </>
          )}
        </div>

        {!loading && !error ? (
          <div className="erp-foot">
            <span>{hasCompletedRun ? 'Run complete' : 'Stored leads'}</span>
            <span className="erp-foot-value">
              {(hasCompletedRun ? scopedLeads.length : leads.length)} rows
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default ExtractionResultPanel;